import React, { useContext, useEffect, useState } from 'react';
import { useEntryFinder } from '~Components/EntryFinder';

import { useLogger, usePromiseQuery, useSelector } from '~Hooks';
import { GET_GIFT_CARD_ITEM, GET_GIFT_CARD_META_DATA } from '~Queries/gift-cards';
import { GetGiftCardMetaData } from '~Queries/__queries__';
import {
  GetGiftCardItem,
  GetGiftCardItemVariables,
  GetGiftCardItem_item,
  GetGiftCardItem_item as GiftCardItem,
} from '~Queries/__queries__/GetGiftCardItem';

interface GiftCardsContext {
  item?: GiftCardItem;
  itemLoading: boolean;
  refetchItemQueries: () => void;
  universeId: string;
}

const Context = React.createContext({} as GiftCardsContext);

export const GiftCardsProvider: React.FunctionComponent = ({ children }) => {
  // Used to indicate fetching in progress
  const [loading, setLoading] = useState<boolean>(true);

  const [metaData, setMetaData] = useState<GetGiftCardMetaData>();
  const [giftCardItem, setGiftCardItem] = useState<GetGiftCardItem_item>();

  const findEntryByItemId = useEntryFinder();

  // We need to check if the POS menu is loaded (findEntryByItemId)
  const POSMenu = useSelector((state) => state.POSMenu);

  // Define queries
  const getMetaData = usePromiseQuery<GetGiftCardMetaData>(GET_GIFT_CARD_META_DATA);
  const getGiftCardItem = usePromiseQuery<GetGiftCardItem, GetGiftCardItemVariables>(
    GET_GIFT_CARD_ITEM,
  );

  const mountLogger = useLogger('mount');

  useEffect(() => {
    mountLogger.info('Mounted: GiftCardsProvider');
    return () => {
      mountLogger.info('Unmounted: GiftCardsProvider');
    };
  }, []);

  // Fetch inital data
  useEffect(() => {
    if (!POSMenu) {
      return;
    }
    fetchGCData();
  }, [getMetaData, getGiftCardItem, POSMenu]);

  const fetchGCData = () => {
    setLoading(true);

    getMetaData({})
      .then((metaDataResponse) => {
        setMetaData(metaDataResponse.data);
        const gcItemId = metaDataResponse.data.operativeViewer.giftcardItemId;

        // Check if the GC item is in the menu
        if (!findEntryByItemId(gcItemId)) {
          throw new Error('Gift card item not part of menu');
        }
        // Make the call for the GC item using the ID from the meta call
        return getGiftCardItem({ id: gcItemId });
      })
      .then((giftCardItemResponse) => {
        setGiftCardItem(giftCardItemResponse.data.item);
      })
      .catch((e) => {
        console.warn(e);
      })
      .finally(() => setLoading(false));
  };

  return (
    <Context.Provider
      value={{
        universeId: metaData?.operativeViewer?.giftcardUniverseId || '',
        item: giftCardItem,
        itemLoading: loading,
        refetchItemQueries: fetchGCData,
      }}>
      {children}
    </Context.Provider>
  );
};

export const useGiftCardsContext = () => useContext(Context);
